/** AI B2B Leads: add the selected leads to a campaign (existing, or created here). Creates contacts only; it never
 *  starts a campaign. The server skips leads with no phone number and numbers already in the campaign. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Loader2, Plus, UserPlus, AlertTriangle } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { flatTimezoneList } from "@/lib/country-timezones";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { phoneKey, type AddToCampaignResult, type ProspectLead } from "./types";
import { countryFromLocation, countryName, DIAL_CODES, regionFromLocale, timezoneForCountry, withCountryCode } from "@shared/dial-codes";
import { useVoiceEngine, visibleAgents } from "@/lib/voice-engine";

// Same list as the ProspectAI page's campaign form (that page keeps its copy private).
const CAMPAIGN_TYPES = [
  "Lead Qualification", "Feedback Collection", "Promotional", "Payment Reminder", "Event Promotion",
  "Appointment Setting", "Customer Survey", "Product Announcement", "Follow-up", "Other",
];

/** A campaign in these states can dial a contact the moment it exists. The server refuses them too. */
const DIALLING = ["running", "in_progress"];

// Every country the dialler can place a number in, for "country for numbers without a country code".
const COUNTRY_OPTIONS = Object.keys(DIAL_CODES)
  .map((iso) => ({ iso, label: `${countryName(iso)} (+${DIAL_CODES[iso]})` }))
  .sort((a, b) => a.label.localeCompare(b.label));

interface CampaignRow { id: string; name: string; status: string; contactCount?: number; totalContacts?: number }
interface AgentRow { id: string; name: string; type: string; isActive?: boolean; voiceProvider?: string | null }

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leads: ProspectLead[];
  onDone: () => void;
  /** Only offer to start a new campaign (the Leads page): leads that were just searched get a campaign of their own. */
  newOnly?: boolean;
}

export function AddToCampaignDialog({ open, onOpenChange, leads, onDone, newOnly = false }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const [mode, setMode] = useState<"existing" | "new">(newOnly ? "new" : "existing");
  const [campaignId, setCampaignId] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState(CAMPAIGN_TYPES[0]);
  const [agentId, setAgentId] = useState("");
  // Used for a number that has no country code and whose lead's location names no country. Starts at the browser's region.
  const [defaultCountry, setDefaultCountry] = useState(() => regionFromLocale(typeof navigator !== "undefined" ? navigator.language : "") ?? "");
  const [timezone, setTimezone] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; }
  });

  // The calling hours follow the leads, not the browser: UAE leads are called in UAE hours. Stops once the person picks one.
  const tzTouched = useRef(false);
  const leadCountry = useMemo(() => {
    const seen = new Map<string, number>();
    for (const l of leads) {
      const c = countryFromLocation(l.location) ?? (defaultCountry || null);
      if (c) seen.set(c, (seen.get(c) ?? 0) + 1);
    }
    return [...seen.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
  }, [leads, defaultCountry]);
  useEffect(() => {
    if (!open || tzTouched.current) return;
    const tz = timezoneForCountry(leadCountry);
    if (tz) setTimezone(tz);
  }, [open, leadCountry]);

  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery<CampaignRow[]>({ queryKey: ["/api/campaigns"], enabled: open, staleTime: 0 });
  const { data: allAgents = [] } = useQuery<AgentRow[]>({
    queryKey: ["/api/agents"],
    enabled: open && mode === "new",
    select: (rows) => rows.filter((a) => a.type === "flow" && a.isActive !== false),
  });
  // With Nova Sonic selected by the super admin, agents that run on ElevenLabs are not offered
  const { novaSelected } = useVoiceEngine();
  const agents = useMemo(() => visibleAgents(allAgents, novaSelected), [allAgents, novaSelected]);

  // Predicts what the server will do. It cannot know about numbers already in the chosen campaign; the server skips those too.
  const summary = useMemo(() => {
    const seen = new Set<string>();
    let willAdd = 0, sharedNumber = 0, noCountryCode = 0, companyLines = 0, noPhone = 0, codeAdded = 0;
    for (const l of leads) {
      // The server adds the same code when it creates the contact, so what is counted here is what will happen.
      const fixed = l.hasPhone ? withCountryCode(l.phone, countryFromLocation(l.location) ?? (defaultCountry || null)) : l.phone;
      const key = l.hasPhone ? phoneKey(fixed) : null;
      if (!key) { noPhone++; continue; }
      if (seen.has(key)) { sharedNumber++; continue; }
      seen.add(key);
      willAdd++;
      if (!String(fixed ?? "").startsWith("+")) noCountryCode++;
      else if (!l.phoneHasCountryCode) codeAdded++;
      if (l.phoneKind === "company") companyLines++;
    }
    return { selected: leads.length, willAdd, noPhone, sharedNumber, noCountryCode, companyLines, codeAdded };
  }, [leads, defaultCountry]);

  const chosen = campaigns.find((c) => c.id === campaignId);
  const chosenDials = !!chosen && DIALLING.includes(String(chosen.status).toLowerCase());
  const canSubmit =
    summary.willAdd > 0 &&
    (mode === "existing" ? !!chosen && !chosenDials : name.trim().length > 0 && !!agentId && !!timezone);

  const add = useMutation({
    mutationFn: async () => {
      let target = campaignId;
      if (mode === "new") {
        const created = await apiRequest("POST", "/api/campaigns", { name: name.trim(), type, agentId, scheduleTimezone: timezone });
        target = (await created.json()).id as string;
        // Calls go out between 09:00 and 20:00 in that timezone: the campaign waits for the window instead of being refused outside it.
        await apiRequest("PATCH", `/api/campaigns/${target}`, { scheduleEnabled: true, scheduleTimeStart: "09:00", scheduleTimeEnd: "20:00", scheduleTimezone: timezone });
      }
      const res = await apiRequest("POST", "/api/prospect-leads/add-to-campaign", { leadIds: leads.map((l) => l.id), campaignId: target, defaultCountry: defaultCountry || undefined });
      return (await res.json()) as AddToCampaignResult;
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["prospect-leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      toast({
        title: t("prospectLeads.campaign.doneTitle", { name: r.campaignName }),
        description: t("prospectLeads.campaign.doneBody", { added: r.added, noPhone: r.skippedNoPhone, dup: r.skippedDuplicate }),
        action: (
          <ToastAction altText={t("prospectLeads.campaign.view")} onClick={() => setLocation(`/app/campaigns/${r.campaignId}`)}>
            {t("prospectLeads.campaign.view")}
          </ToastAction>
        ),
      });
      onDone();
      onOpenChange(false);
    },
    onError: (e: any) => {
      // A new campaign may already exist if only the second step failed, so the list is refreshed either way.
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      toast({ title: t("prospectLeads.campaign.failed"), description: e?.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !add.isPending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[540px]" data-testid="add-to-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5" />{t("prospectLeads.campaign.title")}</DialogTitle>
          <DialogDescription>{t("prospectLeads.campaign.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!newOnly && <RadioGroup value={mode} onValueChange={(v) => setMode(v as "existing" | "new")} className="flex gap-6">
            <div className="flex items-center gap-2"><RadioGroupItem value="existing" id="mode-existing" /><Label htmlFor="mode-existing" className="cursor-pointer">{t("prospectLeads.campaign.existing")}</Label></div>
            <div className="flex items-center gap-2"><RadioGroupItem value="new" id="mode-new" /><Label htmlFor="mode-new" className="cursor-pointer">{t("prospectLeads.campaign.new")}</Label></div>
          </RadioGroup>}

          {mode === "existing" ? (
            <div className="space-y-2">
              <Label>{t("prospectLeads.campaign.choose")}</Label>
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger data-testid="campaign-select"><SelectValue placeholder={campaignsLoading ? "..." : t("prospectLeads.campaign.choose")} /></SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => {
                    const dials = DIALLING.includes(String(c.status).toLowerCase());
                    return (
                      <SelectItem key={c.id} value={c.id} disabled={dials}>
                        {c.name}
                        {dials && ` (${t("prospectLeads.campaign.running")})`}
                        {String(c.status).toLowerCase() === "scheduled" && ` (${t("prospectLeads.campaign.scheduled")})`}
                      </SelectItem>
                    );
                  })}
                  {!campaignsLoading && campaigns.length === 0 && <div className="p-2 text-sm text-muted-foreground">{t("prospectLeads.campaign.none")}</div>}
                </SelectContent>
              </Select>
              {campaigns.some((c) => DIALLING.includes(String(c.status).toLowerCase())) && (
                <p className="text-xs text-muted-foreground">{t("prospectLeads.campaign.runningHint")}</p>
              )}
              {chosen && String(chosen.status).toLowerCase() === "scheduled" && (
                <p className="text-xs text-amber-600 dark:text-amber-400">{t("prospectLeads.campaign.scheduledHint")}</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="new-campaign-name">{t("prospectLeads.campaign.name")}</Label>
                <Input id="new-campaign-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-testid="new-campaign-name" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>{t("prospectLeads.campaign.type")}</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CAMPAIGN_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("prospectLeads.campaign.agent")}</Label>
                  <Select value={agentId} onValueChange={setAgentId}>
                    <SelectTrigger data-testid="new-campaign-agent"><SelectValue placeholder={t("prospectLeads.campaign.agentPlaceholder")} /></SelectTrigger>
                    <SelectContent>
                      {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      {agents.length === 0 && <div className="p-2 text-sm text-muted-foreground">{t("prospectLeads.campaign.noAgents")}</div>}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("prospectLeads.campaign.timezone")}</Label>
                <Select value={timezone} onValueChange={(v) => { tzTouched.current = true; setTimezone(v); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{flatTimezoneList.map((z) => <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{t("prospectLeads.campaign.defaultCountry", "Country for numbers without a country code")}</Label>
            <Select value={defaultCountry || "none"} onValueChange={(v) => setDefaultCountry(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="default-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("prospectLeads.campaign.defaultCountryNone", "None: leave those numbers as they are")}</SelectItem>
                {COUNTRY_OPTIONS.map((c) => <SelectItem key={c.iso} value={c.iso}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t("prospectLeads.campaign.defaultCountryHint", "A lead's own country (from its location) is used first.")}
              {summary.codeAdded > 0 && ' ' + t("prospectLeads.campaign.codeAdded", { count: summary.codeAdded, defaultValue: "{{count}} number(s) will get a country code added." })}
            </p>
          </div>

          <dl className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/40 p-3 text-sm" data-testid="campaign-summary">
            <dt className="text-muted-foreground">{t("prospectLeads.campaign.summary.selected")}</dt><dd className="text-right font-medium">{summary.selected}</dd>
            <dt className="text-muted-foreground">{t("prospectLeads.campaign.summary.willAdd")}</dt><dd className="text-right font-medium text-green-600 dark:text-green-400">{summary.willAdd}</dd>
            <dt className="text-muted-foreground">{t("prospectLeads.campaign.summary.noPhone")}</dt><dd className="text-right font-medium">{summary.noPhone}</dd>
            <dt className="text-muted-foreground">{t("prospectLeads.campaign.summary.sharedNumber")}</dt><dd className="text-right font-medium" data-testid="shared-number-count">{summary.sharedNumber}</dd>
            <dt className="text-muted-foreground">{t("prospectLeads.campaign.summary.noCountryCode")}</dt><dd className="text-right font-medium">{summary.noCountryCode}</dd>
            <dt className="text-muted-foreground">{t("prospectLeads.campaign.summary.companyLines")}</dt><dd className="text-right font-medium">{summary.companyLines}</dd>
          </dl>

          {summary.companyLines > 0 && (
            <Alert data-testid="company-line-warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{t("prospectLeads.campaign.companyLineHint")}</AlertDescription>
            </Alert>
          )}

          {summary.noCountryCode > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{t("prospectLeads.campaign.countryCodeHint")}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={add.isPending}>{t("prospectLeads.delete.cancel")}</Button>
          <Button onClick={() => add.mutate()} disabled={!canSubmit || add.isPending} data-testid="confirm-add-to-campaign">
            {add.isPending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("prospectLeads.campaign.working")}</>) : mode === "new" ? (<><Plus className="mr-2 h-4 w-4" />{t("prospectLeads.campaign.submitNew", { count: summary.willAdd })}</>) : t("prospectLeads.campaign.submitExisting", { count: summary.willAdd })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
